<template>
  <section class="n-view-login h-screen p-10 flex justify-center items-center">
    <NCard class="w-full max-w-[450px] shadow-md">
      <div class="text-center">
        <img class="mx-auto p-8" src="/images/logo-dark.png" alt="" />
      </div>

      <ATabs v-model:activeKey="activeKey" size="large" type="card" centered :tab-bar-gutter="10" @change="tabChange">
        <ATabPane key="live" :tab="tl('liveSystem')">
          <NForm id="" ref="form" :model="model" :rules="rules" class="!grid-cols-1" @keydown.enter="onPost()">
            <NInputPreSuffix v-model:value="model.username" v-bind="usernameAttrs">
              <template #suffix>
                <mail-outlined />
              </template>
            </NInputPreSuffix>
            <NInputSafePassword v-model:value="model.password" v-bind="passwordAttrs" />
            <div class="flex space-x-2"></div>
            <NButton class="n-login-button" size="large" block type="primary" :loading="loading" @click="onPost()">
              <span>{{ $tl('login') }}</span>
            </NButton>
          </NForm>
        </ATabPane>
        <ATabPane key="demo" :tab="tl('demoSystem')">
          <NForm id="" ref="formVir" :model="modelVir" :rules="rulesVir" class="!grid-cols-1" @keydown.enter="onPostVir()">
            <NInputPreSuffix v-model:value="modelVir.usernameVir" v-bind="usernameVirAttrs">
              <template #suffix>
                <mail-outlined />
              </template>
            </NInputPreSuffix>
            <NInputSafePassword v-model:value="modelVir.passwordVir" v-bind="passwordVirAttrs" />
            <div class="flex space-x-2"></div>
            <NButton class="n-login-button" size="large" block type="primary" :loading="loadingVir" @click="onPostVir()">
              <span>{{ $tl('login') }}</span>
            </NButton>
          </NForm>
        </ATabPane>
      </ATabs>

      <div class="py-2 text-right pr-2">
        <span class="mr-5">
          <RouterLink to="/forgot">{{ $tl('forgotPassword') }}</RouterLink>
        </span>
      </div>
      <div class="py-2 text-center text-base">
        <span class="mr-2">{{ $tl('noAccount') }}</span>
        <RouterLink to="/register">{{ $tl('goRegister') }}</RouterLink>
      </div>
      <div>
        <NDivider class="text-base">{{ $tl('loginQuick') }}</NDivider>
      </div>

      <ARow justify="center">
        <ACol :span="10" :pull="1">
          <NButton no-loading class="n-third-redirect-button" @click="onGoogle()">
            <div class="flex items-center justify-center space-x-2">
              <NImage :src="'/images/login-google.png'" class="rounded-full shadow" :preview="false" :width="24" />
              <div class="pt-1">Google</div>
            </div>
          </NButton>
        </ACol>
        <ACol :span="10" :push="1">
          <NButton no-loading class="n-third-redirect-button" @click="onFacebook()">
            <div class="flex items-center justify-center space-x-2">
              <NImage :src="'/images/login-facebook.png'" class="rounded-full shadow" :preview="false" :width="24" />
              <div class="pt-1">Facebook</div>
            </div>
          </NButton>
        </ACol>
      </ARow>

      <div class="mt-10 mb-3 text-center text-gray-600 grid items-center gap-3 grid-flow-col place-content-center">
        {{ COPYRIGHT }}
        <NChannelSwitch />
        <NLanguageSwitcher />
      </div>
    </NCard>
  </section>
</template>

<script setup>
import {useLanguage, useModelPost, useRouter, useValidate} from '@front/hooks';
import {ssoLogin} from '@front/api/sso';
import {computed, onMounted, reactive, ref} from 'vue';
import {COPYRIGHT, SSO_ENVS} from '@front/constant';
import {Col as ACol, Row as ARow, TabPane as ATabPane, Tabs as ATabs} from 'ant-design-vue/es';
import {getAdminURL, getFrontURL, setDevice} from '@/services';
import dsbridge from 'dsbridge';

const { changeLanguage, curLang, tl } = useLanguage();
const { router, routerTo } = useRouter();
const query = router.currentRoute.value.query;
// [URI]?env=demo/live&lang=en/zh/tw/ja/ko/pt&device=2eccae9f0dc95b0d

// http://localhost:3000/pages/front.html#/ssoLogin?device=8;2eccae9f0dc95b0d&env=demo&lang=en&url=http://www.baidu.com
// https://dev-ccms-web.nextop.cn/#/ssoLogin?device=8;2eccae9f0dc95b0d&env=demo&lang=zh&url=http://www.baidu.com

const front = getFrontURL();
const admin = getAdminURL();

const configs = reactive({
  url: 'http://www.baidu.com',
  env: SSO_ENVS.LIVE,
  device: '[NA]',
  lang: 'zh',
});

urlConfig();

const activeKey = ref(configs.env);
// const isLive = computed(() => activeKey.value === SSO_ENVS.LIVE);
// const isDemo = computed(() => activeKey.value === SSO_ENVS.DEMO);

const model = reactive({ username: '', password: '' });
const modelVir = reactive({ usernameVir: '', passwordVir: '' });
const remoteGetMethodName = 'getConfigs';
const remotePutMethodName = 'onResponse';

onMounted(() => {
  if (dsbridge.hasNativeMethod(remoteGetMethodName)) {
    console.log(`dsbridge.hasNativeMethod :${remoteGetMethodName}`);
    dsbridge.call(remoteGetMethodName, null, r => {
      console.log('sso Result:', r);
      const settings = JSON.parse(r);
      if (settings.lang) configs.lang = settings.lang;
      if (settings.env) configs.env = settings.env;
      if (settings.url) configs.url = settings.url;
      if (settings.device) configs.device = settings.device;
      changeLanguage(configs.lang);
      setDevice(configs.device);
    });
  } else {
    console.log(`dsbridge no ${remoteGetMethodName} method, set configs with url param`);
    changeLanguage(configs.lang);
    setDevice(configs.device);
  }
});

function onLogon(token) {
  const params = {
    token: token,
    url: configs.url,
    env: configs.env,
    lang: curLang.value,
    result: 'success',
  };

  console.log('logon params', params);

  if (dsbridge.hasNativeMethod(remotePutMethodName)) {
    console.log(`dsbridge.hasNativeMethod :${remotePutMethodName}`);
    dsbridge.call(remotePutMethodName, params, r => {
      // response {"data": {"result": "success"}}
      const data = r.data;
      console.log('onLogon dsBridge response data', data);
    });
  } else {
    console.log(`dsbridge no ${remotePutMethodName} method, params:`, params);
  }
}

const { form, loading, onPost } = useModelPost({
  model,
  post: async () => {
    const result = await customLogin(model);
    onLogon(result.token);
  },
});

const {
  form: formVir,
  loading: loadingVir,
  onPost: onPostVir,
} = useModelPost({
  modelVir,
  post: async () => {
    const result = await customLoginVir(modelVir);
    onLogon(result.token);
  },
});

const validation = computed(() => ({
  username: { required: true, isEmail: true },
  password: { required: true },
}));

const validationVir = computed(() => ({
  usernameVir: { required: true, isEmail: true },
  passwordVir: { required: true },
}));

const { rules } = useValidate({ formRef: form, validation });
const { rules: rulesVir } = useValidate({ formRef: formVir, validation: validationVir });

const usernameAttrs = computed(() => ({
  size: 'large',
  name: 'username',
  placeholder: tl('loginUsername'),
  disabled: loading.value,
}));

const passwordAttrs = computed(() => ({
  size: 'large',
  name: 'password',
  placeholder: tl('loginPassword'),
  disabled: loading.value,
  type: 'password',
}));

const usernameVirAttrs = computed(() => ({
  size: 'large',
  name: 'usernameVir',
  placeholder: tl('loginUsername'),
  disabled: loadingVir.value,
}));

const passwordVirAttrs = computed(() => ({
  size: 'large',
  name: 'passwordVir',
  placeholder: tl('loginPassword'),
  disabled: loadingVir.value,
  type: 'password',
}));

const onGoogle = () => {
  window.open(
    admin + '/oauth2/authorization/google?action=LOGIN&redirectUrl=' + front + '/pages/front.html%23/externalLoginVerify',
    '_self',
  );
};
const onFacebook = () => {
  window.open(
    admin + '/oauth2/authorization/facebook?action=LOGIN&redirectUrl=' + front + '/pages/front.html%23/externalLoginVerify',
    '_self',
  );
};

function urlConfig() {
  if (query.url) configs.url = query.url;
  if (query.env) configs.env = query.env;
  if (query.lang) configs.lang = query.lang;
  if (query.device) configs.device = query.device;
}

function tabChange(v) {}

async function customLogin(model) {
  const params = {
    username: model.username,
    password: model.password,
  };
  return await ssoLogin(params);
}

async function customLoginVir(modelVir) {
  const params = {
    username: modelVir.usernameVir,
    password: modelVir.passwordVir,
  };
  return await ssoLogin(params);
}
</script>

<style lang="less">
.n-view-login {
  background-image: url('/images/shape01.png');
  background-position: center;

  .n-language-switcher {
    gap: 6px;
  }

  .n-gutter-box-text {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .n-third-redirect-button {
    width: 100%;
    height: fit-content;
  }

  .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab-active {
    font-weight: bold;
  }
}
</style>
